import { Send } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge, StatusBadge, pathFor,
  Diagram, MockField, MockSelect, MockButton,
} from "../shared";

export default function FormsSendForReviewSection() {
  return (
    <div data-testid="guide-forms-send-for-review">
      <PageHeading
        icon={Send}
        title="Send for Review"
        description="One click to push an in-progress task into the MD review queue. It changes the status and the holder together, so nothing gets left half-done."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="member" />
        <span className="text-xs text-white/40">The owner, the current responsible person, or any admin.</span>
      </div>

      <Callout variant="info" title="In plain words">
        When your work is ready for the MD to look at, this button does the whole handover in <span className="text-white">one step</span> — it both moves the task to the review stage and hands it to the right place. Before, you had to do those two things separately and it was easy to forget one.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>When an in-progress task is finished and ready for SDF MD (or SDF PMO) review.</li>
          <li>It only shows on tasks currently <StatusBadge status="IN_PROGRESS" />. It's hidden once a task is with the MD, closed, cancelled, or in the upward-dispatch path.</li>
        </ul>
      </Section>

      <Section title="What it looks like">
        <Diagram
          frameLabel="Updates → Send for Review"
          legend={[
            <>Click the green <span className="text-white">Send for Review</span> button (it sits just before the Hand-to dropdown).</>,
            <>Pick a <span className="text-white">destination</span> — To SDF MD or To SDF PMO.</>,
            <>Anything in the progress-update box becomes the handoff note automatically.</>,
          ]}
        >
          <MockButton label="Send for Review" marker={1} />
          <MockSelect
            label="Destination"
            value="To SDF MD"
            marker={2}
            open
            options={["To SDF MD", "To SDF PMO"]}
          />
          <MockField label="Note (optional — auto-captured)" placeholder="Pack finalised, ready for MD review." marker={3} />
        </Diagram>
      </Section>

      <Section title="Step-by-step">
        <Steps
          steps={[
            <>Open the <span className="text-white">Updates</span> page and find your in-progress task. Click the green <span className="inline-flex items-center gap-1"><Send className="w-3 h-3 text-emerald-300" /> Send for Review</span> button (just before the Hand-to dropdown).</>,
            <>In the small menu, choose a <span className="text-white">destination</span>: To SDF MD or To SDF PMO.</>,
            <>Optionally type a note in the progress box first — it's captured automatically as the handoff note. Then confirm.</>,
          ]}
        />
      </Section>

      <Section title="Every box explained">
        <Field name="Destination" required="required">
          <p>Pick <span className="text-white">To SDF MD</span> when the SDF MD is the next reviewer, or <span className="text-white">To SDF PMO</span> when the PMO needs to triage first.</p>
          <p className="text-white/40">Targets are detected dynamically — the menu only shows destinations that exist as a team member or authority lane in your system.</p>
        </Field>

        <Field name="Note" required="optional">
          <p>Anything you typed into the progress-update box on the same row is captured automatically as the handoff note.</p>
          <p className="text-white/40">Leaving it blank is fine — the record still shows who sent the item where.</p>
        </Field>
      </Section>

      <NextStep>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>The status moves to <StatusBadge status="WITH_SDF_MD_REVIEW" /> in one step (no separate handoff).</li>
          <li>The "Currently With" pointer updates to your chosen destination.</li>
          <li>A single combined event records both the status change and the responsibility change.</li>
          <li>The receiving person gets one email — not two.</li>
          <li>The item appears on the <Link href={pathFor("pages/md-meetings")} className="text-cyan-300 hover:underline">MD Meetings</Link> review queue immediately.</li>
        </ul>
      </NextStep>

      <Callout variant="tip" title="Why this exists">
        Before this button, users had to use the Hand-to dropdown <em>and</em> the Transition-to dropdown separately. It was easy to do one and forget the other — leaving items with the wrong status or no clear holder. Send for Review does both in one click and writes a single combined record.
      </Callout>

      <Callout variant="warning" title="Why ceremonial names aren't in the regular Hand-to dropdown">
        SDF MD, SDF PMO, SDF Board Secretary, EDGE Chairman, EDGE MD Office, and Board / ExCom are <em>workflow stages</em>, not people you can quietly assign to. Using the regular Hand-to dropdown to push items to these names used to leave them stuck (no status change → never on the MD Meetings queue). They're now hidden from the Hand-to dropdown and the sub-task assignee dropdown — use Send for Review (or the proper status transition on the detail page) to route items into a ceremonial stage.
      </Callout>
    </div>
  );
}
